import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Languages, Eye, EyeOff } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_PREFIX = "e65_tr_";

// Quick heuristic to skip translating obvious same-language content.
function probablySameLang(text, target) {
  if (!text || text.length < 12) return true;
  if (target === "en") {
    // very lightweight: if string already contains lots of common English words, skip
    const tokens = text.toLowerCase().split(/\W+/);
    const en = ["the", "and", "is", "of", "to", "a", "i", "for", "with", "you"];
    const hits = tokens.filter((t) => en.includes(t)).length;
    return hits >= 2;
  }
  return false;
}

/**
 * Inline auto-translated text with a toggle "Show original / Show translation".
 * - Calls POST /api/translate (backend caches in MongoDB).
 * - Also caches the result in localStorage for offline-speed.
 * - Shows a small "🌐 translated" badge with the language code.
 */
export default function TranslatedText({ text, className = "", dataTestId, as: Tag = "div" }) {
  const { lang, t } = useI18n();
  const [translated, setTranslated] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [busy, setBusy] = useState(false);
  const fetched = useRef(false);

  useEffect(() => {
    if (!text || !lang) return;
    fetched.current = false;
    setTranslated(null);
    setShowOriginal(false);

    if (probablySameLang(text, lang)) return;

    const cacheKey = LS_PREFIX + lang + "_" + btoa(unescape(encodeURIComponent(text))).slice(0, 200);
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) { setTranslated(cached); return; }
    } catch { /* ignore */ }

    const tid = setTimeout(async () => {
      if (fetched.current) return;
      fetched.current = true;
      setBusy(true);
      try {
        const { data } = await axios.post(`${API}/translate`, { text, target_lang: lang });
        if (data?.translated && data.translated !== text) {
          setTranslated(data.translated);
          try { localStorage.setItem(cacheKey, data.translated); } catch { /* ignore */ }
        }
      } catch { /* ignore */ } finally { setBusy(false); }
    }, 80);
    return () => clearTimeout(tid);
  }, [text, lang]);

  const showTranslation = !!translated && !showOriginal;
  const display = showTranslation ? translated : text;

  return (
    <Tag data-testid={dataTestId} className={className}>
      <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{display}</span>
      {translated && (
        <button
          type="button"
          data-testid={dataTestId ? `${dataTestId}-toggle` : undefined}
          onClick={(e) => { e.stopPropagation(); setShowOriginal((v) => !v); }}
          className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#00AFFF]/10 border border-[#00AFFF]/30 text-[#00AFFF] text-[9px] font-display font-bold uppercase tracking-wider hover:bg-[#00AFFF]/20 transition align-middle"
          title={showOriginal ? t("common.show_translation", "Show translation") : t("common.show_original", "Show original")}
        >
          <Languages size={9} />
          {showOriginal ? (lang.toUpperCase()) : <><EyeOff size={9}/> orig</>}
        </button>
      )}
      {busy && !translated && (
        <span className="ml-2 inline-flex items-center gap-1 text-[9px] text-white/40">
          <Languages size={9} className="animate-pulse" /> translating…
        </span>
      )}
    </Tag>
  );
}
